import InputError from '@/components/layout/InputError';
import { Input } from '@/components/ui/shadcn/input';
import { Label } from '@/components/ui/shadcn/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/shadcn/select';
import { useId } from 'react';

interface EmailDomainInputProps {
    label?: string;
    value: string;
    onChange: (value: string) => void;
    approvedDomains: string[];
    error?: string;
    required?: boolean;
    disabled?: boolean;
    readOnly?: boolean;
    placeholder?: string;
    autoComplete?: string;
    tabIndex?: number;
}

export default function EmailDomainInput({
    label = 'Email address',
    value,
    onChange,
    approvedDomains,
    error,
    required = false,
    disabled = false,
    readOnly = false,
    placeholder = 'email',
    autoComplete = 'email',
    tabIndex,
}: EmailDomainInputProps) {
    const inputId = useId();

    // Split email into local part and domain
    const getEmailParts = (email: string) => {
        const atIndex = email.lastIndexOf('@');
        if (atIndex === -1) {
            return { localPart: email, domain: '' };
        }
        return {
            localPart: email.substring(0, atIndex),
            domain: email.substring(atIndex + 1),
        };
    };

    const { localPart, domain } = getEmailParts(value);

    const handleLocalPartChange = (newLocalPart: string) => {
        const newEmail = domain ? `${newLocalPart}@${domain}` : newLocalPart;
        onChange(newEmail);
    };

    const handleDomainChange = (newDomain: string) => {
        const newEmail = localPart ? `${localPart}@${newDomain}` : `@${newDomain}`;
        onChange(newEmail);
    };

    const isReadOnly = readOnly || disabled;

    return (
        <div className="grid gap-2">
            <Label htmlFor={inputId}>{label}</Label>
            <div className="flex rounded-md">
                <Input
                    id={inputId}
                    type="text"
                    placeholder={placeholder}
                    value={localPart}
                    onChange={(e) => handleLocalPartChange(e.target.value)}
                    disabled={isReadOnly}
                    readOnly={readOnly}
                    required={required && !readOnly}
                    autoComplete={autoComplete}
                    tabIndex={tabIndex}
                    className={`-me-px rounded-r-none shadow-none focus-visible:z-1 ${readOnly ? 'bg-muted cursor-not-allowed' : ''}`}
                />
                {approvedDomains.length > 0 ? (
                    <Select value={domain} onValueChange={handleDomainChange} disabled={isReadOnly}>
                        <SelectTrigger className={`w-auto min-w-[120px] rounded-l-none shadow-none ${readOnly ? 'bg-muted cursor-not-allowed' : ''}`}>
                            <SelectValue placeholder="@domain.com" />
                        </SelectTrigger>
                        <SelectContent>
                            {approvedDomains.map((domainOption) => (
                                <SelectItem key={domainOption} value={domainOption} className="pr-2 [&_svg]:hidden">
                                    @{domainOption}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                ) : (
                    <Input
                        type="text"
                        placeholder="@domain.com"
                        value={domain ? `@${domain}` : ''}
                        onChange={(e) => {
                            const domainValue = e.target.value.startsWith('@') ? e.target.value.substring(1) : e.target.value;
                            handleDomainChange(domainValue);
                        }}
                        disabled={isReadOnly}
                        readOnly={readOnly}
                        className={`w-auto min-w-[120px] rounded-l-none shadow-none ${readOnly ? 'bg-muted cursor-not-allowed' : ''}`}
                    />
                )}
            </div>
            <InputError message={error} />
        </div>
    );
}
